import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEdit, FaCog, FaTrash } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import { DEFAULT_PLAN_CONFIG } from './AdminPlanConfigConstants';
import '../styles/admin-theme.css';

const AdminPlans = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    is_active: true,
  });
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminSchemaApi.fetchPlans();
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      code: '',
      name: '',
      description: '',
      is_active: true,
    });
    setShowModal(true);
    setError('');
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code || '',
      name: row.name || '',
      description: row.description || '',
      is_active: row.is_active !== false,
    });
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm({ code: '', name: '', description: '', is_active: true });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code?.trim() || !form.name?.trim()) {
      setError('Code and name are required.');
      return;
    }
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description?.trim() || undefined,
      config: editing ? undefined : DEFAULT_PLAN_CONFIG,
      is_active: form.is_active,
    };
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await adminSchemaApi.updatePlan(editing.id, payload);
      } else {
        await adminSchemaApi.createPlan(payload);
      }
      handleCloseModal();
      await loadList();
    } catch (err) {
      setError(err?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id || !window.confirm('Delete this plan? This cannot be undone.')) return;
    setDeleteId(id);
    setDeleting(true);
    setError('');
    try {
      await adminSchemaApi.deletePlan(id);
      setDeleteId(null);
      await loadList();
    } catch (err) {
      setError(err?.message || 'Failed to delete plan');
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (row) => {
    setError('');
    try {
      await adminSchemaApi.updatePlan(row.id, { is_active: !row.is_active });
      await loadList();
    } catch (err) {
      setError(err?.message || 'Failed to update plan');
    }
  };

  return (
    <>
      <h2 className="admin-title">Plans</h2>
      <p className="admin-subtitle">
        Manage plans (e.g. basic, standard, enterprise). Code must be unique. Inactive plans are hidden from selection where applicable.
      </p>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <div className="mb-3">
        <Button className="admin-primary-btn" onClick={openCreate}>
          New Plan
        </Button>
      </div>

      {loading ? (
        <p className="admin-subtitle">Loading...</p>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Active</th>
              <th style={{ width: 420, minWidth: 420 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((row) => (
              <tr key={row.id}>
                <td>{row.code || '—'}</td>
                <td>{row.name || '—'}</td>
                <td>{row.is_active ? 'Yes' : 'No'}</td>
                <td className="text-nowrap">
                  <Button
                    variant={row.is_active ? 'outline-danger' : 'outline-success'}
                    size="sm"
                    className="me-1"
                    title={row.is_active ? 'Deactivate plan' : 'Activate plan'}
                    onClick={() => handleToggleActive(row)}
                  >
                    {row.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Link to={`/admin/plans/${row.id}/detail`} className="btn btn-sm admin-logout-btn me-1" title="View plan details and prices">
                    Detail
                  </Link>
                  <Link to={`/admin/plans/${row.id}/licenses`} className="btn btn-sm admin-logout-btn me-1" title="Manage plan licenses">
                    Licenses
                  </Link>
                  <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEdit(row)} title="Edit details">
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="me-1"
                    onClick={() => handleDelete(row.id)}
                    disabled={deleting && deleteId === row.id}
                  >
                    <FaTrash />
                  </Button>
                  <Button variant="outline-primary" size="sm" as={Link} to={`/admin/plans/${row.id}`} title="Configure plan restrictions">
                    <FaCog />
                  </Button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  No plans. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Plan' : 'New Plan'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Code</Form.Label>
              <Form.Control
                type="text"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                placeholder="e.g. basic, standard, enterprise"
                required
                disabled={!!editing}
              />
              {editing && <Form.Text className="text-muted">Code cannot be changed after create.</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Basic Plan"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional"
              />
            </Form.Group>
            {!editing && (
              <Form.Text className="text-muted d-block mb-2">
                After creating, use Configure to set plan restrictions (tabs, fields, objects, setup menu, etc.).
              </Form.Text>
            )}
            <Form.Check
              type="checkbox"
              id="plan-is-active"
              label="Active"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" className="admin-primary-btn" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AdminPlans;
